import type { MutableRefObject, PointerEvent } from 'react'
import type { BagControl } from '@/components/three/snack-bag'

import { useCallback, useRef } from 'react'

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))

export const createBagControl = (overrides: Partial<BagControl> = {}): BagControl => ({
    rotationY: 0,
    rotationX: 0,
    zoom: 1,
    autoRotate: false,
    dragging: false,
    velocity: 0,
    ...overrides
})

/* Drag to turn a 3D package, with a little momentum after letting go */
export const useBagDrag = (control: MutableRefObject<BagControl>) => {
    const last = useRef<{ x: number, y: number, time: number } | null>(null)

    const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
        if (event.button !== 0 && event.pointerType === 'mouse') return

        event.currentTarget.setPointerCapture(event.pointerId)
        last.current = { x: event.clientX, y: event.clientY, time: performance.now() }
        control.current.dragging = true
        control.current.autoRotate = false
        control.current.velocity = 0
    }, [control])

    const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
        const previous = last.current

        if (!previous) return

        const now = performance.now()
        const dx = event.clientX - previous.x
        const dy = event.clientY - previous.y
        const seconds = Math.max(8, now - previous.time) / 1000

        control.current.rotationY += dx * 0.012
        control.current.rotationX = clamp(control.current.rotationX + dy * 0.006, -0.45, 0.45)
        control.current.velocity = clamp((dx * 0.012) / seconds * 0.5, -10, 10)
        last.current = { x: event.clientX, y: event.clientY, time: now }
    }, [control])

    const onPointerEnd = useCallback(() => {
        if (!last.current) return

        last.current = null
        control.current.dragging = false
    }, [control])

    return {
        onPointerDown,
        onPointerMove,
        onPointerUp: onPointerEnd,
        onPointerCancel: onPointerEnd,
        onLostPointerCapture: onPointerEnd
    }
}
